import React, { useContext } from "react";
import Dropdown from "components/dropdown";
import { FiAlignJustify } from "react-icons/fi";
import { Link } from "react-router-dom";
import { MdLock, MdChevronRight } from "react-icons/md";
import { RiMoonFill, RiSunFill } from "react-icons/ri";
import avatar from "assets/img/avatars/avatar4.png";
import avatarMiri from "assets/img/avatars/Escudo_Argentinos_Juniors.svg";
import { AuthContext } from "context/AuthContext";
import { isUserLoggedIn } from "utils/auth";
import { useTheme } from "utils/theme";

import AdminAvisoEditor from "components/AdminAvisoEditor";
import LogoutButton from "components/LogoutButton";

/**
 * Barra superior.
 *
 * Antes convivían dos contenedores redondeados (un breadcrumb suelto con un
 * título de 33px y una píldora flotante con las acciones) que competían entre
 * sí. Ahora es una única barra de 64px anclada arriba: rastro de navegación a
 * la izquierda, identidad y acciones a la derecha. El título de la página vive
 * en el encabezado del contenido, donde le corresponde por jerarquía.
 */
const Navbar = (props: {
  onOpenSidenav: () => void;
  brandText: string;
  secondary?: boolean | string;
}) => {
  const { onOpenSidenav, brandText } = props;

  // El tema vive en utils/theme: se persiste y se comparte con el resto de los
  // controles, así el icono nunca queda invertido respecto del estado real.
  const { isDark, toggleTheme } = useTheme();

  const { user } = useContext(AuthContext);
  const firstName = user?.firstName || "";
  const lastName = user?.lastName || "";
  const fullName = `${firstName} ${lastName}`.trim();

  return (
    <header className="sticky top-0 z-30 select-none border-b border-gray-200/70 bg-white/75 backdrop-blur-xl dark:border-white/10 dark:bg-navy-900/70">
      <div className="mx-auto flex h-16 w-full max-w-[1400px] items-center justify-between gap-4 px-4 md:px-6 lg:px-8">
      {/* Izquierda: menú mobile + breadcrumb */}
      <div className="flex min-w-0 items-center gap-2">
        <button
          type="button"
          aria-label="Abrir menú"
          onClick={onOpenSidenav}
          className="-ml-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-control text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/10 xl:hidden"
        >
          <FiAlignJustify className="h-[18px] w-[18px]" />
        </button>

        <nav aria-label="Ruta" className="flex min-w-0 items-center gap-1">
          <a
            className="rounded-control px-1.5 py-1 text-[13px] font-medium text-gray-700 transition-colors hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
            href="/"
          >
            Inicio
          </a>
          <MdChevronRight
            className="h-4 w-4 shrink-0 text-gray-600 dark:text-gray-600"
            aria-hidden="true"
          />
          <span className="truncate px-1.5 py-1 text-[13px] font-semibold text-gray-900 dark:text-white">
            {brandText}
          </span>
        </nav>
      </div>

      {/* Derecha: usuario, tema y menú de cuenta */}
      <div className="flex shrink-0 items-center gap-1.5">
        {fullName ? (
          <span className="mr-1 hidden truncate rounded-full bg-gray-50 px-3 py-1.5 text-[13px] font-medium text-gray-700 dark:bg-white/5 dark:text-gray-300 sm:inline-block">
            {fullName}
          </span>
        ) : null}

        <button
          type="button"
          onClick={toggleTheme}
          aria-label={isDark ? "Usar tema claro" : "Usar tema oscuro"}
          className="flex h-9 w-9 items-center justify-center rounded-control text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/10"
        >
          {isDark ? (
            <RiSunFill className="h-[18px] w-[18px]" />
          ) : (
            <RiMoonFill className="h-[18px] w-[18px]" />
          )}
        </button>

        <Dropdown
          button={
            <button
              type="button"
              aria-label="Menú de cuenta"
              className="ml-0.5 flex h-9 w-9 items-center justify-center rounded-full ring-1 ring-gray-200 transition-shadow hover:ring-gray-300 dark:ring-white/15 dark:hover:ring-white/30"
            >
              <img
                className="h-8 w-8 cursor-pointer rounded-full object-cover"
                src={firstName === "Marcos" ? avatarMiri : avatar}
                alt=""
              />
            </button>
          }
          classNames={"top-12 right-0 w-max"}
        >
          <div className="w-60 overflow-hidden rounded-card border border-gray-200/80 bg-white shadow-pop dark:border-white/10 dark:!bg-navy-700 dark:text-white">
            <div className="px-4 py-3">
              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                {fullName ? `Hola, ${firstName}` : "Invitado"}
              </p>
              <p className="mt-0.5 truncate text-[12px] text-gray-700 dark:text-gray-400">
                {user?.username
                  ? `@${user.username}`
                  : "Sesión no iniciada"}
              </p>
            </div>

            <div className="h-px w-full bg-gray-200/80 dark:bg-white/10" />

            <div className="p-2">
              {user?.username === "admin" && <AdminAvisoEditor />}

              {!isUserLoggedIn() ? (
                <Link
                  to="/inicio-sesion"
                  className="flex w-full items-center gap-3 rounded-control px-2 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-white/5 dark:hover:text-white"
                >
                  <MdLock className="h-[18px] w-[18px] shrink-0 text-gray-600 dark:text-gray-500" />
                  Iniciar Sesión
                </Link>
              ) : (
                <LogoutButton variant="menu" />
              )}
            </div>
          </div>
        </Dropdown>
      </div>
      </div>
    </header>
  );
};

export default Navbar;
